import { useState } from 'react';
import { studyApi, type AuthArgs } from '../api/studyApi';
import type { DepthAdvice, LineLevel } from '../../../shared/types';

/**
 * "Go deeper" — raise an opening's level by one rung.
 *
 * The catalog has two axes: which opening to learn, and how far into it to go.
 * The pathway used to walk only the first, which meant nothing in the app ever
 * said "go back to the Italian and take on the intermediate variations". This
 * is the control that does, and it appears in every place the question comes
 * up so the answer is the same in all of them.
 *
 * The decision itself is the server's: `DepthAdvice` arrives already resolved,
 * with the next level, how many lines it adds, and the reason when it is not
 * the right moment. The button is never hidden when blocked, only disabled with
 * the reason next to it — a control that vanishes leaves people wondering
 * whether the feature exists, and a reason is what makes advice worth following
 * rather than something to route around.
 *
 * Nothing here is a lock. The pathway's opinion is a suggestion, and the level
 * can still be set from the opening page whenever someone disagrees with it.
 */
export default function DeepenControl({
  openingId,
  name,
  depth,
  auth,
  onChanged,
  compact = false,
}: {
  openingId: string;
  name: string;
  depth: DepthAdvice;
  auth: AuthArgs;
  /** Called after the level has actually moved, so callers can refetch. */
  onChanged: (level: LineLevel) => void | Promise<void>;
  compact?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ level: LineLevel; lines: number } | null>(null);
  const [error, setError] = useState('');

  // At the top of the ladder there is nothing to offer and nothing to explain.
  // This is deliberately not a disabled button: "complete" and "blocked" look
  // identical if you grey them both out, and they mean opposite things.
  if (!depth.nextLevel) {
    return (
      <div className={`deepen${compact ? ' deepen--compact' : ''}`}>
        {done && (
          <p className="deepen-done" role="status">
            <strong>{done.level} unlocked.</strong> {done.lines} more{' '}
            {done.lines === 1 ? 'variation' : 'variations'} of {name}{' '}
            {done.lines === 1 ? 'is' : 'are'} now in your schedule.
          </p>
        )}
        {!compact && (
          <p className="deepen-note muted">You are at the deepest level this opening has.</p>
        )}
      </div>
    );
  }

  const next = depth.nextLevel;

  async function deepen() {
    setBusy(true);
    setError('');
    try {
      await studyApi.setRepertoireSelection(openingId, { level: next }, auth);
      setDone({ level: next, lines: depth.nextLevelLines });
      await onChanged(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change the level');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`deepen${compact ? ' deepen--compact' : ''}`}>
      {/* The confirmation sits above the control rather than replacing it.
          Swapping the button out for a receipt reads as "that is done now" and
          quietly removes the next rung until the page is reloaded, which is the
          opposite of what someone who has just chosen to go deeper wants. */}
      {done && (
        <p className="deepen-done" role="status">
          <strong>{done.level} unlocked.</strong> {done.lines} more{' '}
          {done.lines === 1 ? 'variation' : 'variations'} of {name}{' '}
          {done.lines === 1 ? 'is' : 'are'} now in your schedule.
        </p>
      )}
      <button
        type="button"
        className={`btn${depth.canDeepen ? ' btn--primary' : ''}`}
        disabled={!depth.canDeepen || busy}
        onClick={deepen}
        // The reason belongs on the control as well as beside it: a disabled
        // button is exactly what a screen reader skips past silently.
        title={depth.blockedReason ?? undefined}
      >
        {busy ? 'Unlocking…' : `Go deeper: ${next}`}
        <span className="deepen-count"> +{depth.nextLevelLines}</span>
      </button>
      {depth.blockedReason && <p className="deepen-why muted">{depth.blockedReason}</p>}
      {depth.canDeepen && depth.resetsCustomSelection && (
        <p className="deepen-why muted">
          You have picked variations by hand here. Moving the level puts this opening back on the
          ladder and replaces that selection.
        </p>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}
